import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useGame, api } from '../context/GameContext';
import { useNavigate } from 'react-router-dom';
import { 
  Coins, Eye, Gift, Users, Send, Check, X,
  Trophy, Palette, CalendarDays, ChevronRight, ChevronDown, Star, Calendar, Zap, User, Image, Flame
} from 'lucide-react';
import GameTooltip from '../components/GameTooltip';
import ChessRook from '../components/ChessRook';
import CardRevealModal from '../components/CardRevealModal';
import DiscoverPreviewModal from '../components/DiscoverPreviewModal';
import LiveCoinPill from '../components/LiveCoinPill';
import SectionLoader from '../components/SectionLoader';
import thumb from '../utils/thumb';

const EarnPage = () => {
  const { user, setUser, t, language, refreshUser, haptic, tutorialStep, setTutorialStep, flashCoinReward, consumePrefetch } = useGame();
  const navigate = useNavigate();
  // Hydrate from sessionStorage so revisiting Earn shows the previous
  // payload instantly while the background refresh runs. The cache key
  // includes the user id so switching accounts doesn't bleed data.
  const cacheKey = user?.id ? `earnCache:${user.id}` : null;
  // Boot-time prefetch: GameContext fired these requests in parallel
  // with auth, so we can render with real data immediately on first
  // mount. `consumePrefetch` is single-use — subsequent visits in the
  // same session fall back to sessionStorage / fresh fetch.
  //
  // TUTORIAL CAVEAT: The boot prefetch fires BEFORE the player claims
  // their first tutorial card. At that moment the server returns a
  // random discover list (no tutorial reorder yet). After the player
  // claims their first card, the server promotes the player's own
  // painting to index 0 of the discover list. If we hydrated from the
  // stale snapshot, the player would briefly see a random card on top
  // and then watch it swap with their own card the instant the fresh
  // fetch arrives — a visible reorder where the previous top card
  // gets pushed down one slot. To avoid this, we IGNORE the boot /
  // sessionStorage payload when the player is currently in the
  // 'earn' or 'discover_claim' tutorial step, and wait for the fresh
  // fetch to populate the (already-reordered) list.
  const tutorialDiscoverPhase = tutorialStep === 'earn' || tutorialStep === 'discover_claim';
  const boot = useMemo(() => {
    if (tutorialDiscoverPhase) {
      // Still consume the prefetch slots so the cache doesn't leak
      // into a later visit, but discard the payload.
      if (consumePrefetch) {
        consumePrefetch('/earn/me');
        consumePrefetch('/daily-bonus/me');
      }
      return null;
    }
    const earn = consumePrefetch ? consumePrefetch('/earn/me') : null;
    const bonus = consumePrefetch ? consumePrefetch('/daily-bonus/me') : null;
    if (earn || bonus) return { earn, bonus };
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const cached = useMemo(() => {
    if (tutorialDiscoverPhase) return null;
    if (!cacheKey) return null;
    try {
      const raw = sessionStorage.getItem(cacheKey);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);
  const [earnData, setEarnData] = useState(boot?.earn || cached?.earn || null);
  const [loading, setLoading] = useState(!cached && !boot);
  const [progress, setProgress] = useState(0);
  const [claimingCard, setClaimingCard] = useState(null);
  const [claimingRef, setClaimingRef] = useState(false);
  const [claimingAch, setClaimingAch] = useState(null);
  const [dailyBonus, setDailyBonus] = useState(boot?.bonus || cached?.bonus || null);
  const [claimingBonus, setClaimingBonus] = useState(false);
  const [claimingAllTasks, setClaimingAllTasks] = useState(false);
  const [revealedTaskCard, setRevealedTaskCard] = useState(null);
  const [claimingBoosts, setClaimingBoosts] = useState(false);
  const [claimingGift, setClaimingGift] = useState(false);
  const [claimingRefIncome, setClaimingRefIncome] = useState(false);
  const [claimingSub, setClaimingSub] = useState(false);
  const [refreshTimer, setRefreshTimer] = useState(0);
  const visitingCardRef = useRef(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [skipLoading, setSkipLoading] = useState(false);
  const [discoverPreview, setDiscoverPreview] = useState(null);

  // Coin pill animation now lives inside <LiveCoinPill /> — explicit
  // rewards route through the context flash bus.
  const triggerCoinReward = useCallback((amount, opts = {}) => {
    flashCoinReward(amount, opts);
  }, [flashCoinReward]);

  const BOT_LINK = 'https://t.me/privateart_bot';

  const loadEarnData = useCallback(async () => {
    if (!user?.id) return;
    try {
      const pending = [api.get(`/earn/me`), api.get(`/daily-bonus/me`)];
      let done = 0;
      pending.forEach((p) => p.finally(() => {
        done += 1;
        setProgress(Math.round(100 * done / pending.length));
      }).catch(() => {}));
      const [earnRes, bonusRes] = await Promise.all(pending);
      setEarnData(earnRes.data);
      setDailyBonus(bonusRes.data);
      // Cache for the next visit — sessionStorage is per-tab and
      // wiped on page reload, which matches the freshness we want
      // (player navigates away and back → instant render; full reload
      // → fresh data).
      try {
        if (cacheKey) {
          sessionStorage.setItem(cacheKey, JSON.stringify({ earn: earnRes.data, bonus: bonusRes.data }));
        }
      } catch { /* quota exceeded etc. — skip cache */ }
    } catch (err) {
      console.error('Error loading earn data:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id, cacheKey]);;

  useEffect(() => {
    loadEarnData();
  }, [loadEarnData]);

  // Discover refresh countdown timer
  useEffect(() => {
    const seconds = earnData?.daily?.discover?.next_refresh_in;
    if (seconds && seconds > 0) {
      setRefreshTimer(seconds);
      const interval = setInterval(() => {
        setRefreshTimer(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            loadEarnData(); // Reload when timer expires
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [earnData?.daily?.discover?.next_refresh_in]);

  // Track return from post visit
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && visitingCardRef.current) {
        const cardId = visitingCardRef.current;
        visitingCardRef.current = null;
        // Optimistic: flip the card to "visited" locally so the Claim
        // button is enabled immediately. Server confirmation + earn
        // refresh happens in the background, removing the 2-3s wait
        // the player used to see between returning from the post and
        // being able to tap Claim.
        setEarnData(prev => {
          if (!prev?.daily?.discover) return prev;
          const updated = { ...prev, daily: { ...prev.daily, discover: { ...prev.daily.discover } } };
          updated.daily.discover.cards = updated.daily.discover.cards.map(c =>
            c.id === cardId ? { ...c, visited: true } : c
          );
          return updated;
        });
        // Fire-and-forget; reconciled by the next loadEarnData. We
        // still refresh because /visit may invalidate other parts of
        // the earn payload, but we don't await it on the hot path.
        api.post(`/earn/me/discover/${cardId}/visit`)
          .then(() => loadEarnData())
          .catch(err => console.error('Error marking visit:', err));
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [user?.id, loadEarnData]);

  const handleDiscoverClick = (card) => {
    if (haptic) haptic();
    // Open in-app preview instead of navigating out of the Mini App.
    // Direct-Link users (coming from a channel button) lose the bot
    // from their chat list when we leave via openTelegramLink, so we
    // keep them inside and let them claim right here.
    setDiscoverPreview(card);
  };

  // Set of card ids the player has just answered correctly in this
  // session. We need this because `loadEarnData()` re-fetches the
  // payload from the server, which doesn't know about the per-card
  // quiz verdict — without this ref the "Correct · x3" lobby badge
  // would flash for a moment and then disappear on the next refresh.
  const quizCorrectIdsRef = useRef(new Set());
  // Mirror set for wrong answers — drives the red X badge in the
  // lobby. Kept separate from `claimed` because a claim can also
  // come from a non-quiz card where neither badge applies.
  const quizWrongIdsRef = useRef(new Set());

  const handleDiscoverClaimed = (response) => {
    if (response?.error) {
      alert(response.error);
      loadEarnData();
      return;
    }
    const cardId = response?.cardId;
    // Remember the verdict locally so the green/blue indicator survives
    // the background reload.
    if (cardId && response?.correct_index !== null && response?.correct_index !== undefined) {
      if (response?.answered_correctly) {
        quizCorrectIdsRef.current.add(cardId);
      } else {
        quizWrongIdsRef.current.add(cardId);
      }
    }
    const reward = response?.coins_earned
      ?? earnData?.daily?.discover?.reward_per_card
      ?? 50;
    // The server already committed the claim before answering, so paint
    // the result and the coins right away instead of waiting on the
    // /earn/me + /users/me round trip — those now only reconcile.
    if (cardId) {
      setEarnData(prev => {
        if (!prev?.daily?.discover) return prev;
        const updated = { ...prev, daily: { ...prev.daily, discover: { ...prev.daily.discover } } };
        updated.daily.discover.cards = (updated.daily.discover.cards || []).map(c =>
          c.id === cardId ? { ...c, claimed: true } : c
        );
        updated.daily.discover.claimed_count = (updated.daily.discover.claimed_count || 0) + 1;
        return updated;
      });
    }
    if (setUser) {
      setUser(prev => prev ? { ...prev, coins: (prev.coins || 0) + reward } : prev);
    }
    triggerCoinReward(reward);
    if (tutorialStep === 'discover_claim') {
      setTutorialStep('upgrades');
    }
    refreshUser().catch(() => { /* reconcile only */ });
    loadEarnData().catch(() => { /* reconcile only */ });
  };

  const handleClaimDiscover = async (cardId) => {
    if (haptic) haptic();
    setClaimingCard(cardId);

    // Optimistic UX: trigger the coin-reward animation and bump the
    // local UI state synchronously. The server confirms in the
    // background — `refreshUser` runs without awaiting so the floating
    // coins appear instantly instead of after a 400–700ms round trip.
    const expectedReward = earnData?.daily?.discover?.reward_per_card || 10;
    triggerCoinReward(expectedReward);
    // Local card flip + counter bump (already done previously, but now
    // happens BEFORE the await so the modal closes onto a fully-updated
    // discover list).
    setEarnData(prev => {
      if (!prev) return prev;
      const updated = { ...prev, daily: { ...prev.daily, discover: { ...prev.daily.discover } } };
      updated.daily.discover.cards = updated.daily.discover.cards.map(c =>
        c.id === cardId ? { ...c, claimed: true } : c
      );
      updated.daily.discover.claimed_count = (updated.daily.discover.claimed_count || 0) + 1;
      return updated;
    });
    // Bump local user.coins so the header pill updates without waiting
    // for the /users/me round trip. We use the functional form so a
    // refreshUser() that resolves before us doesn't get clobbered.
    if (setUser) {
      setUser(prev => prev ? { ...prev, coins: (prev.coins || 0) + expectedReward } : prev);
    }

    if (tutorialStep === 'discover_claim') {
      setTutorialStep('upgrades');
    }

    try {
      const res = await api.post(`/earn/me/discover/${cardId}/claim`);
      // If server returned a different reward (anti-cheat / discounts),
      // reconcile by adding the delta — never re-add the full amount.
      const actualReward = res.data?.coins_earned ?? expectedReward;
      const delta = actualReward - expectedReward;
      if (delta !== 0 && setUser) {
        setUser(prev => prev ? { ...prev, coins: (prev.coins || 0) + delta } : prev);
      }
      // Fire-and-forget reconciliation with the authoritative user object.
      refreshUser().catch(() => { /* noop */ });
    } catch (err) {
      // Roll back the optimistic update on failure.
      if (setUser) {
        setUser(prev => prev ? { ...prev, coins: Math.max(0, (prev.coins || 0) - expectedReward) } : prev);
      }
      setEarnData(prev => {
        if (!prev) return prev;
        const updated = { ...prev, daily: { ...prev.daily, discover: { ...prev.daily.discover } } };
        updated.daily.discover.cards = updated.daily.discover.cards.map(c =>
          c.id === cardId ? { ...c, claimed: false } : c
        );
        updated.daily.discover.claimed_count = Math.max(0, (updated.daily.discover.claimed_count || 0) - 1);
        return updated;
      });
      alert(err.response?.data?.detail || 'Error');
    } finally {
      setClaimingCard(null);
    }
  };

  const handleClaimReferral = async () => {
    if (haptic) haptic();
    setClaimingRef(true);
    try {
      const res = await api.post(`/earn/me/referral/claim`);
      await refreshUser();
      await loadEarnData();
      triggerCoinReward(res.data.coins_earned);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error');
    } finally {
      setClaimingRef(false);
    }
  };

  const handleClaimAchievement = async (achId) => {
    if (haptic) haptic();
    setClaimingAch(achId);
    try {
      const res = await api.post(`/earn/me/achievement/${achId}/claim`);
      await refreshUser();
      await loadEarnData();
      triggerCoinReward(res.data.coins_earned);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error');
    } finally {
      setClaimingAch(null);
    }
  };

  const shareViaTelegram = () => {
    if (!earnData?.daily?.referral?.referral_code) return;
    if (haptic) haptic();
    const code = earnData.daily.referral.referral_code;
    const shareUrl = `${BOT_LINK}?start=ref_${code}`;
    const shareText = `Join me on Private Art!\nGet a free painting and turn into Stars ⭐`;
    const telegramShareUrl = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;
    const tg = window.Telegram?.WebApp;
    if (tg?.openTelegramLink) {
      tg.openTelegramLink(telegramShareUrl);
    } else {
      window.open(telegramShareUrl, '_blank');
    }
  };

  const [notifyToggling, setNotifyToggling] = useState(false);
  const [notifyArmed, setNotifyArmed] = useState(false);
  // After tap we briefly show the "checked" state, then fade out and
  // unmount. `notifyExiting` controls the fade-out animation; we keep
  // the button mounted for `EXIT_MS` so the player sees their tap is
  // confirmed before it disappears.
  const [notifyExiting, setNotifyExiting] = useState(false);
  const NOTIFY_FADE_MS = 250;

  // Sync local toggle with server state whenever earn data refreshes.
  useEffect(() => {
    setNotifyArmed(!!earnData?.daily?.discover?.notify_when_ready);
  }, [earnData?.daily?.discover?.notify_when_ready]);

  const handleNotifyToggle = async () => {
    if (notifyToggling || notifyArmed || notifyExiting) return;
    if (haptic) haptic();
    setNotifyToggling(true);
    // Start the fade + collapse animation immediately so the panel
    // doesn't have the awkward "button gone, panel resizes 500ms later"
    // gap. The wrapper's max-height + opacity + margin transition in
    // sync, then unmount happens at the end of NOTIFY_FADE_MS.
    setNotifyArmed(true);
    setNotifyExiting(true);
    setTimeout(() => setNotifyExiting(false), NOTIFY_FADE_MS);
    try {
      await api.post('/earn/me/discover/notify-toggle', { enabled: true });
    } catch (err) {
      // Roll back on failure
      setNotifyArmed(false);
      setNotifyExiting(false);
      const detail = err.response?.data?.detail;
      if (detail) alert(detail);
    } finally {
      setNotifyToggling(false);
    }
  };

  const handleSkipTimer = async () => {
    if (!user?.id || skipLoading) return;
    if (haptic) haptic();
    setSkipLoading(true);
    try {
      const res = await api.post(`/earn/me/discover/skip-timer`);
      const invoiceLink = res.data?.invoice_link;
      if (!invoiceLink) throw new Error('No invoice link');
      
      const tg = window.Telegram?.WebApp;
      if (tg?.openInvoice) {
        tg.openInvoice(invoiceLink, async (status) => {
          if (status === 'paid') {
            await api.post(`/earn/me/discover/skip-confirm`);
            setRefreshTimer(0);
            await loadEarnData();
            await refreshUser();
          }
          setSkipLoading(false);
        });
      } else {
        window.open(invoiceLink, '_blank');
        setSkipLoading(false);
      }
    } catch (err) {
      console.error('Skip timer error:', err);
      setSkipLoading(false);
    }
  };

  const handleClaimBonus = async () => {
    if (!user?.id || claimingBonus || !dailyBonus?.can_claim) return;
    if (haptic) haptic();
    setClaimingBonus(true);
    try {
      const res = await api.post(`/daily-bonus/me/claim`);
      const earned = res.data?.coins_earned || res.data?.coins || dailyBonus?.next_bonus || 0;
      // Instant feedback: the claim itself takes ~0.1s, but awaiting the
      // full earn refresh (Telegram membership/boost checks) held the UI
      // for seconds. Paint the result now, re-sync in the background.
      triggerCoinReward(earned);
      setDailyBonus((b) => (b ? {
        ...b,
        can_claim: false,
        consecutive_days: res.data?.consecutive_days ?? b.consecutive_days,
      } : b));
      setClaimingBonus(false);
      refreshUser();
      loadEarnData();
    } catch (err) {
      console.error('Error claiming bonus:', err);
      setClaimingBonus(false);
    }
  };

  const handleClaimAllTasks = async () => {
    if (haptic) haptic();
    setClaimingAllTasks(true);
    try {
      const res = await api.post(`/earn/me/all-tasks-bonus/claim`);
      await refreshUser();
      await loadEarnData();
      triggerCoinReward(res.data.coins_earned);
      // If backend returned a rare painting, open the reveal modal.
      if (res.data?.card) {
        setRevealedTaskCard(res.data.card);
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Error');
    } finally {
      setClaimingAllTasks(false);
    }
  };

  const handleClaimBoosts = async () => {
    if (haptic) haptic();
    setClaimingBoosts(true);
    try {
      const res = await api.post(`/earn/me/boosts/claim`);
      await refreshUser();
      await loadEarnData();
      triggerCoinReward(res.data.coins_earned);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error');
    } finally {
      setClaimingBoosts(false);
    }
  };

  const handleClaimGift = async () => {
    if (haptic) haptic();
    setClaimingGift(true);
    try {
      const res = await api.post(`/earn/me/gift/claim`);
      await refreshUser();
      await loadEarnData();
      triggerCoinReward(res.data.coins_earned);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error');
    } finally {
      setClaimingGift(false);
    }
  };

  const handleClaimReferralIncome = async () => {
    if (haptic) haptic();
    setClaimingRefIncome(true);
    try {
      const res = await api.post(`/earn/me/referral-income/claim`);
      await refreshUser();
      await loadEarnData();
      triggerCoinReward(res.data.coins_earned);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error');
    } finally {
      setClaimingRefIncome(false);
    }
  };

  const handleClaimSubscribe = async () => {
    if (haptic) haptic();
    setClaimingSub(true);
    try {
      const res = await api.post(`/earn/me/subscribe/claim`);
      await refreshUser();
      await loadEarnData();
      triggerCoinReward(res.data.coins_earned);
    } catch (err) {
      const detail = err.response?.data?.detail;
      // The button is always tappable (the cached membership flag used to
      // hide it for minutes after subscribing) — the server does the live
      // check, so explain the result instead of a raw error.
      const msg = detail === 'Not subscribed to @privateart'
        ? getText('subscribe_first')
        : (detail || 'Error');
      import('sonner').then(({ toast }) => toast.error(msg));
      await loadEarnData();
    } finally {
      setClaimingSub(false);
    }
  };


  const getText = (key) => {
    const texts = {
      earn: { en: 'Earn', ru: 'Заработать', zh: '赚取' },
      daily_tasks: { en: 'Daily Tasks', ru: 'Ежедневные', zh: '每日任务' },
      achievements: { en: 'Achievements', ru: 'Достижения', zh: '成就' },
      discover_art: { en: 'Discover Art', ru: 'Открывать картины', zh: '发现艺术' },
      invite_friends: { en: 'Invite Friends', ru: 'Пригласить друзей', zh: '邀请朋友' },
      discover: { en: 'Discover', ru: 'Открыть', zh: '发现' },
      claim: { en: 'Claim', ru: 'Забрать', zh: '领取' },
      subscribe_first: {
        en: 'Subscribe to @privateart first, then tap again',
        ru: 'Сначала подпишись на @privateart, потом нажми ещё раз',
        es: 'Primero suscríbete a @privateart y vuelve a tocar',
        ar: 'اشترك أولاً في @privateart ثم اضغط مرة أخرى',
        uk: 'Спершу підпишись на @privateart, потім натисни ще раз',
        fr: "Abonnez-vous d'abord à @privateart, puis retapez",
        pt: 'Inscreva-se em @privateart primeiro e toque novamente',
      },
      claimed: { en: 'Claimed', ru: 'Получено', zh: '已领取' },
      coins: { en: 'coins', ru: 'монет', zh: '金币' },
      same_artist: { en: 'Same Artist Collection', ru: 'Коллекция одного художника', zh: '同一艺术家收藏' },
      same_year: { en: 'Same Year Collection', ru: 'Коллекция одного года', zh: '同年收藏' },
      collect_5_artist: { en: 'Collect 5 paintings by one artist', ru: 'Собрать 5 картин одного художника', zh: '收集同一艺术家的5幅画' },
      collect_5_year: { en: 'Collect 5 paintings from one year', ru: 'Собрать 5 картин одного года', zh: '收集同一年的5幅画' },
      per_friend: { en: 'per friend', ru: 'за друга', zh: '每位朋友' },
      today: { en: 'today', ru: 'сегодня', zh: '今天' },
      share: { en: 'Share', ru: 'Поделиться', zh: '分享' },
      claim_rewards: { en: 'Claim Rewards', ru: 'Забрать награду', zh: '领取奖励' },
      all_tasks: { en: 'Tasks Bonus', ru: 'Бонус за задачи', zh: '任务奖励' },
      complete_all: { en: 'Complete all daily tasks to earn bonus', ru: 'Выполните все задачи дня', zh: '完成所有每日任务获取奖励' },
      boost_channel: { en: 'Boost Channel', ru: 'Буст канала', zh: '频道助力' },
      boost_desc: { en: 'Boost @privateart and earn coins', ru: 'Забустите @privateart и получите монеты', zh: '助力频道获取金币' },
      boost_btn: { en: 'Boost', ru: 'Забустить', zh: '助力' },
      gift_channel: { en: 'Gift Channel', ru: 'Подарок каналу', zh: '赠送频道' },
      gift_desc: { en: 'Send a gift to @privateart and earn coins', ru: 'Подарите гифт каналу @privateart', zh: '向频道赠送礼物' },
      gift_btn: { en: 'Send Gift', ru: 'Подарить', zh: '赠送' },
      subscribe_channel: { en: 'Subscribe Channel', ru: 'Подписка на канал', zh: '订阅频道' },
      subscribe_desc: { en: 'Subscribe to @privateart and earn coins', ru: 'Подпишитесь на @privateart и получите монеты', zh: '订阅频道获取金币' },
      subscribe_btn: { en: 'Subscribe', ru: 'Подписаться', zh: '订阅' },
    };
    return texts[key]?.[language] || texts[key]?.['en'] || key;
  };

  if (loading) {
    return <SectionLoader progress={progress} language={language} />;
  }

  const discover = earnData?.daily?.discover;
  const referral = earnData?.daily?.referral;
  const achievements = earnData?.achievements;
  const refClaimable = referral ? Math.min(referral.today_invited, referral.max_daily || 10) - referral.today_claimed : 0;

  return (
    <div className="pb-24 px-4">
      {/* Header — sticky */}
      <div className="sticky top-0 z-40 bg-[#05050A] pt-4 pb-3 -mx-4 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GameTooltip text={t('your_coins')} position="bottom">
              <LiveCoinPill />
            </GameTooltip>
            <GameTooltip text={t('total_influence')} position="bottom">
              <div className="flex items-center gap-1.5 glass-card px-2.5 py-1.5 rounded-full cursor-pointer">
                <ChessRook className="w-3.5 h-3.5 text-[#D4AF37]" />
                <span className="font-mono text-xs tabular-nums">{user?.total_rating || 0}</span>
              </div>
            </GameTooltip>
            <GameTooltip text={t('painting_slots')} position="bottom">
              <div className="flex items-center gap-1.5 glass-card px-2.5 py-1.5 rounded-full cursor-pointer">
                <Image className="w-3.5 h-3.5 text-[#A1A1AA]" />
                <span className="font-mono text-xs tabular-nums">{user?.cards_count || 0}/{user?.max_paintings || 1}</span>
              </div>
            </GameTooltip>
          </div>
          <button
            data-testid="profile-btn-earn"
            onClick={() => { haptic(); navigate('/profile'); }}
            className="w-8 h-8 rounded-full glass-card border border-white/10 flex items-center justify-center hover:border-[#D4AF37]/50 transition-colors"
          >
            {user?.photo_url
              ? <img src={user.photo_url} alt="" className="w-8 h-8 rounded-full object-cover" />
              : <User className="w-4 h-4 text-[#A1A1AA]" />
            }
          </button>
        </div>
      </div>

      {/* Daily Tasks Section */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <CalendarDays className="w-4 h-4 text-[#D4AF37]" />
          <h2 className="text-sm font-medium text-[#A1A1AA] uppercase tracking-wider">{getText('daily_tasks')}</h2>
        </div>

        {/* Discover Art */}
        <div data-testid="discover-section" className="glass-card rounded-2xl p-4 mb-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-[#D4AF37]" />
              <h3 className="font-medium text-[#F5F5F7] text-sm">{getText('discover_art')}</h3>
            </div>
            <span className="text-xs text-[#A1A1AA]">
              {discover?.claimed_count || 0}/{discover?.max || 5}
            </span>
          </div>

          {/* Next refresh timer + Skip button */}
          {refreshTimer > 0 && (discover?.claimed_count || 0) >= (discover?.max || 5) && (
            <div className="flex items-center justify-center gap-3 mb-2 py-1">
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-[#A1A1AA]">{language === 'ru' ? 'Обновление через' : 'Next in'}</span>
                <span className="text-[11px] font-mono text-[#D4AF37]/70">
                  {Math.floor(refreshTimer / 3600)}:{String(Math.floor((refreshTimer % 3600) / 60)).padStart(2, '0')}:{String(refreshTimer % 60).padStart(2, '0')}
                </span>
              </div>
              <button
                data-testid="skip-timer-btn"
                onClick={handleSkipTimer}
                disabled={skipLoading}
                className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#5B68F6]/15 border border-[#5B68F6]/30 text-[10px] font-medium text-[#7B8AFF] hover:bg-[#5B68F6]/25 transition-all active:scale-95 disabled:opacity-50"
              >
                {skipLoading ? '...' : (
                  <>
                    <span>Skip</span>
                    <span>⭐</span>
                    <span>{discover?.skip_price || Math.max(1, Math.ceil(refreshTimer / 3600))}</span>
                  </>
                )}
              </button>
            </div>
          )}

          <div className="space-y-2">
            {discover?.cards?.map((card, idx) => {
              // Tutorial highlights the first card. The hint arrow +
              // pulsing glow now sit on the Discover button (the
              // step we want the player to take) — previously they
              // sat on the Claim button, which was confusing because
              // the player has to open Discover FIRST.
              const isTutorialFirstCard = tutorialStep === 'discover_claim' && idx === 0;
              return (
              <div
                key={card.id}
                data-testid={`discover-card-${card.id}`}
                className={`flex items-center gap-3 bg-[#0a0a12] rounded-xl p-2.5 relative ${isTutorialFirstCard ? 'tut-halo' : ''}`}
              >
                <img
                  src={thumb(card.image_url, 160)}
                  alt={card.title || 'Art'}
                  className="w-10 h-10 rounded-lg object-cover shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-[#F5F5F7] truncate">{card.title}</p>
                  {/* Reveal: show "Correct · x3" green badge once the
                      player has answered the quiz correctly. The set
                      is in a ref so it survives `loadEarnData()`
                      refreshes (the server doesn't track per-card
                      quiz history). Hide the artist by default
                      because the modal quiz needs it secret. */}
                  {card.claimed && quizCorrectIdsRef.current.has(card.id) ? (
                    <p
                      data-testid={`discover-correct-${card.id}`}
                      className="text-[10px] text-[#10B981] truncate"
                    >
                      {language === 'ru' ? 'Верно' : 'Correct'} · x5
                    </p>
                  ) : !card.question?.type && card.artist ? (
                    <p className="text-[10px] text-[#A1A1AA] truncate">{card.artist}</p>
                  ) : null}
                </div>
                <div className="shrink-0 relative">
                  {/* Tutorial arrow now points at the Discover button.
                      Only render when the Discover button is the one
                      visible (i.e. card not yet visited and not yet
                      claimed). After the player opens the modal and
                      answers, the tutorialStep transitions away from
                      'discover_claim' so the arrow disappears. */}
                  {isTutorialFirstCard && !card.claimed && !card.visited && (
                    <div
                      data-testid="tutorial-arrow-discover"
                      className="absolute -top-7 left-1/2 -translate-x-1/2 pointer-events-none animate-tut-arrow z-20"
                    >
                      <ChevronDown className="w-7 h-7 text-[#F5D07A]" strokeWidth={2.4} style={{ filter: 'drop-shadow(0 0 10px rgba(245,208,122,0.8))' }} />
                    </div>
                  )}
                  {card.claimed ? (
                    quizWrongIdsRef.current.has(card.id) ? (
                      <span
                        data-testid={`discover-wrong-${card.id}`}
                        className="text-[10px] text-[#EF4444] bg-[#EF4444]/10 px-2 py-1 rounded-full"
                      >
                        <X className="w-3 h-3 inline" />
                      </span>
                    ) : (
                      <span className="text-[10px] text-[#10B981] bg-[#10B981]/10 px-2 py-1 rounded-full">
                        <Check className="w-3 h-3 inline" />
                      </span>
                    )
                  ) : card.visited ? (
                    <button
                      onClick={() => handleClaimDiscover(card.id)}
                      disabled={claimingCard === card.id || (tutorialStep === 'discover_claim' && idx !== 0)}
                      data-testid={`claim-discover-${card.id}`}
                      className="text-[10px] font-medium text-white bg-[#10B981] px-3 py-1 rounded-full transition-all active:scale-95"
                    >
                      {claimingCard === card.id ? '...' : `${getText('claim')} +${discover?.reward_per_card ?? 10}`}
                    </button>
                  ) : (
                    <button
                      onClick={() => handleDiscoverClick(card)}
                      // Disable OTHER cards' Discover buttons during
                      // tutorial, but keep the FIRST card's button
                      // active — that's the one the arrow points at.
                      disabled={tutorialStep === 'discover_claim' && idx !== 0}
                      data-testid={`discover-btn-${card.id}`}
                      className={`text-[10px] font-medium px-3 py-1 rounded-full transition-all active:scale-95 hover:bg-[#D4AF37]/10 ${
                        isTutorialFirstCard
                          ? 'tut-halo border border-[#F5D07A]/70 text-[#F5D07A] bg-[#F5D07A]/[0.07]'
                          : 'text-[#D4AF37] border border-[#D4AF37]/40'
                      }`}
                    >
                      {getText('discover')}
                    </button>
                  )}
                </div>
              </div>
            );
            })}
          </div>

          {/* Notify-when-ready toggle: placed AFTER the cards list because
              the player's eye flows top→bottom: paintings → wait timer →
              "ping me when it resets". Visible only while:
                - the current batch is fully claimed,
                - the timer is running,
                - the toggle is OFF (or in the brief out-animation
                  window after just-armed). One-shot subscription —
                  server clears the flag when DM fires. */}
          {refreshTimer > 0 &&
           (discover?.claimed_count || 0) >= (discover?.max || 5) &&
           (!notifyArmed || notifyExiting) && (
            <div
              className={`flex justify-center overflow-hidden transition-all duration-300 ease-out ${
                notifyExiting
                  ? 'opacity-0 max-h-0 mt-0 pointer-events-none'
                  : 'opacity-100 max-h-12 mt-3 animate-in fade-in slide-in-from-top-1 duration-300'
              }`}
            >
              <button
                data-testid="discover-notify-toggle"
                onClick={handleNotifyToggle}
                disabled={notifyToggling || notifyExiting}
                className={`flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-medium transition-all active:scale-95 disabled:opacity-100 ${
                  notifyArmed
                    ? 'bg-[#10B981]/15 border border-[#10B981]/40 text-[#34D399]'
                    : 'bg-transparent border border-[#A1A1AA]/30 text-[#A1A1AA] hover:border-[#A1A1AA]/55 hover:text-[#F5F5F7]'
                }`}
              >
                {/* Checkbox: empty square in OFF state, filled with
                    accent + checkmark in ON state. Built inline so we
                    can match the gold/green palette of the rest of the
                    Earn page without a new icon dependency. */}
                <span
                  className={`inline-flex items-center justify-center w-3.5 h-3.5 rounded-[3px] border transition-colors ${
                    notifyArmed
                      ? 'bg-[#10B981] border-[#10B981]'
                      : 'bg-transparent border-[#A1A1AA]/70'
                  }`}
                  aria-hidden="true"
                >
                  {notifyArmed && (
                    <svg
                      viewBox="0 0 16 16"
                      className="w-2.5 h-2.5 text-[#0C0C14]"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="3 8 7 12 13 4" />
                    </svg>
                  )}
                </span>
                <span>
                  {language === 'ru' ? 'Уведомить, когда обновится' : 'Notify when ready'}
                </span>
              </button>
            </div>
          )}
        </div>

        {/* Invite Friends */}
        <div data-testid="invite-section" className="glass-card rounded-2xl p-4 mb-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-[#D4AF37]" />
              <h3 className="font-medium text-[#F5F5F7] text-sm">{getText('invite_friends')}</h3>
            </div>
            <span className="text-xs text-[#A1A1AA]">
              {Math.min(referral?.today_invited || 0, 10)}/{referral?.max_daily || 10} {getText('today')}
            </span>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="bg-[#0a0a12] rounded-xl p-2.5">
              <p className="text-[9px] text-[#A1A1AA] mb-0.5">{language === 'ru' ? 'Друзей приглашено' : 'Friends Invited'}</p>
              <p className="font-mono text-sm text-[#F5F5F7]">{referral?.total_referred || 0}</p>
            </div>
            <div className="bg-[#0a0a12] rounded-xl p-2.5">
              <p className="text-[9px] text-[#A1A1AA] mb-0.5">{language === 'ru' ? 'Доход от друзей' : 'Income from Friends'}</p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <Coins className="w-3 h-3 text-[#D4AF37]" />
                  <p className="font-mono text-sm text-[#D4AF37]">{referral?.pending_referral_income || 0}</p>
                </div>
                <button
                  data-testid="claim-referral-income-btn"
                  onClick={handleClaimReferralIncome}
                  disabled={claimingRefIncome || !(referral?.pending_referral_income > 0)}
                  className="px-2 py-0.5 rounded-md bg-[#10B981] text-white text-[9px] font-medium transition-all active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  {claimingRefIncome ? '...' : getText('claim')}
                </button>
              </div>
            </div>
          </div>

          <p className="text-xs text-[#A1A1AA] mb-1">
            +{referral?.reward_per_friend || 10000} {getText('coins')} {getText('per_friend')}
          </p>
          <p className="text-[10px] text-[#52525B] mb-3">
            {t('friends_income_desc') || "+2% of friend's income from exhibitions"}
          </p>

          <div className="flex gap-2">
            <button
              data-testid="share-telegram-btn"
              onClick={shareViaTelegram}
              className="flex-1 py-2.5 rounded-xl btn-gold flex items-center justify-center gap-2 text-sm"
            >
              <Send className="w-4 h-4" />
              <span>{getText('invite_friends')}</span>
            </button>
            {refClaimable > 0 && (
              <button
                data-testid="claim-referral-btn"
                onClick={handleClaimReferral}
                disabled={claimingRef}
                className="flex-1 py-2.5 rounded-xl bg-[#10B981] text-white flex items-center justify-center gap-1.5 text-sm font-medium transition-all active:scale-95"
              >
                <Gift className="w-4 h-4" />
                <span>+{refClaimable * (referral?.reward_per_friend || 10000)}</span>
              </button>
            )}
          </div>
        </div>

        {/* Daily Bonus */}
        <div data-testid="daily-bonus-section" className="glass-card rounded-2xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-[#D4AF37]" />
              <h3 className="font-medium text-sm text-[#F5F5F7]">{t('daily_bonus')}</h3>
            </div>
            <button onClick={() => setShowCalendar(!showCalendar)} className="text-sm text-[#D4AF37]">
              <Calendar className="w-4 h-4" />
            </button>
          </div>

          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs text-[#A1A1AA]">{t('consecutive_days')}</p>
              <p className="font-mono text-xl text-[#F5F5F7]">{dailyBonus?.consecutive_days || 0}/30</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-[#A1A1AA]">{dailyBonus?.can_claim ? t('today_bonus') : t('next_bonus')}</p>
              <div className="flex items-center gap-1 justify-end">
                <Coins className="w-3.5 h-3.5 text-[#D4AF37]" />
                <span className="font-mono text-lg text-[#D4AF37]">+{dailyBonus?.next_bonus || 0}</span>
              </div>
            </div>
          </div>

          <button
            data-testid="claim-bonus-btn"
            onClick={handleClaimBonus}
            disabled={claimingBonus || !dailyBonus?.can_claim}
            className={`w-full py-2.5 rounded-xl font-medium text-sm transition-all flex items-center justify-center gap-2 ${
              dailyBonus?.can_claim ? 'bg-[#10B981] text-white' : 'bg-[#141420] text-[#52525B] cursor-not-allowed'
            }`}
          >
            {claimingBonus ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : dailyBonus?.can_claim ? (
              <><Gift className="w-4 h-4" /><span>{t('claim_bonus')}</span></>
            ) : (
              <><Check className="w-4 h-4" /><span>{t('claimed')}</span></>
            )}
          </button>

          {showCalendar && dailyBonus?.calendar && (
            <div className="mt-3 pt-3 border-t border-white/10">
              <div className="grid grid-cols-7 gap-1">
                {dailyBonus.calendar.slice(0, 28).map((day, idx) => (
                  <div
                    key={idx}
                    className={`aspect-square rounded flex items-center justify-center text-[10px] ${
                      day.claimed
                        ? 'bg-[#D4AF37]/20 text-[#D4AF37]'
                        : idx + 1 === (dailyBonus.consecutive_days || 0) + 1 && dailyBonus.can_claim
                        ? 'bg-[#D4AF37] text-[#05050A]'
                        : 'bg-[#141420] text-[#52525B]'
                    }`}
                  >
                    <span className="font-mono">{idx + 1}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* All Tasks Bonus */}
      {(() => {
        const atb = earnData?.daily?.all_tasks_bonus;
        if (!atb) return null;
        const left = (done, target) => Math.max(0, (target || 0) - (done || 0));
        const progressLabel = (done, target) => {
          const remaining = left(done, target);
          if (!target || remaining === 0) return '';
          return language === 'ru'
            ? ` (${done || 0}/${target} · ещё ${remaining})`
            : ` (${done || 0}/${target} · ${remaining} left)`;
        };
        const tasks = [
          {
            done: atb.discover_done,
            label: (language === 'ru' ? 'Открыть 5 картин' : 'Discover 5 paintings')
              + (atb.discover_done ? '' : progressLabel(atb.discover_progress, atb.discover_target)),
            ratio: Math.min(1, (atb.discover_progress || 0) / (atb.discover_target || 5)),
          },
          {
            done: atb.referrals_done,
            label: (language === 'ru' ? 'Пригласить 3 друзей' : 'Invite 3 friends')
              + (atb.referrals_done ? '' : progressLabel(atb.referrals_progress, atb.referrals_target))
              + (!atb.referrals_done && atb.referrals_pending > 0
                ? (language === 'ru'
                  ? ` · заберите ${atb.referrals_pending}`
                  : ` · claim ${atb.referrals_pending}`)
                : ''),
            ratio: Math.min(1, (atb.referrals_progress || 0) / (atb.referrals_target || 3)),
          },
          {
            done: atb.daily_bonus_done,
            label: language === 'ru' ? 'Ежедневный бонус' : 'Daily bonus',
            ratio: atb.daily_bonus_done ? 1 : 0,
          },
        ];
        // Partial tasks still move the bar, so inviting 1 of 3 friends is visible.
        const progressRatio = tasks.reduce((s, t) => s + (t.done ? 1 : t.ratio), 0) / tasks.length;

        return (
          <div data-testid="all-tasks-bonus" className="glass-card rounded-2xl p-4 mb-6 border border-[#D4AF37]/10">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-[#D4AF37]" />
                <h3 className="font-medium text-sm text-[#F5F5F7]">{getText('all_tasks')}</h3>
                {atb.streak > 0 && (
                  <div
                    data-testid="all-tasks-streak"
                    className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-[#F97316]/10 border border-[#F97316]/30"
                    title={
                      language === 'ru'
                        ? `Стрик: ${atb.streak} ${atb.streak === 1 ? 'день' : 'дней'} подряд (рекорд: ${atb.best_streak || atb.streak})`
                        : `Streak: ${atb.streak} day${atb.streak === 1 ? '' : 's'} in a row (best: ${atb.best_streak || atb.streak})`
                    }
                  >
                    <Flame className="w-3 h-3 text-[#F97316]" />
                    <span className="font-mono text-[11px] text-[#F97316] font-semibold">{atb.streak}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  <Coins className="w-3.5 h-3.5 text-[#D4AF37]" />
                  <span className="font-mono text-sm text-[#D4AF37]">+{atb.reward}</span>
                </div>
                <span className="text-[#52525B]">·</span>
                <div
                  className="flex items-center gap-1"
                  data-testid="all-tasks-rare-painting-reward"
                  title={language === 'ru' ? 'Редкая картина (Ур. 10)' : 'Rare painting (Lv. 10)'}
                >
                  <Image className="w-3.5 h-3.5 text-[#3B82F6]" />
                  <span className="font-mono text-[11px] text-[#3B82F6] uppercase tracking-wide">
                    {language === 'ru' ? '+Редкая' : '+Rare'}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-2 mb-3">
              {tasks.map((task, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`all-tasks-item-${i}`}>
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
                    task.done
                      ? 'bg-[#10B981]'
                      : task.ratio > 0
                      ? 'border border-[#D4AF37] bg-[#D4AF37]/15'
                      : 'border border-[#52525B]'
                  }`}>
                    {task.done
                      ? <Check className="w-2.5 h-2.5 text-white" />
                      : task.ratio > 0 && <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />}
                  </div>
                  <span className={`text-xs ${task.done ? 'text-[#A1A1AA] line-through' : 'text-[#F5F5F7]'}`}>
                    {task.label}
                  </span>
                </div>
              ))}
            </div>

            {/* Progress bar */}
            <div className="h-1.5 bg-[#141420] rounded-full overflow-hidden mb-3">
              <div
                data-testid="all-tasks-progress-bar"
                className="h-full bg-[#D4AF37] rounded-full transition-all duration-500"
                style={{ width: `${progressRatio * 100}%` }}
              />
            </div>

            {atb.claimed ? (
              <div className="text-center py-1.5">
                <span className="text-xs text-[#10B981]"><Check className="w-3 h-3 inline mr-1" />{getText('claimed')}</span>
              </div>
            ) : atb.all_done ? (
              <button
                data-testid="claim-all-tasks-btn"
                onClick={handleClaimAllTasks}
                disabled={claimingAllTasks}
                className="w-full py-2.5 rounded-xl bg-[#10B981] text-white font-medium text-sm flex items-center justify-center gap-2 transition-all active:scale-95"
              >
                {claimingAllTasks ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <><Gift className="w-4 h-4" /><span>{getText('claim_rewards')} +{atb.reward}</span></>
                )}
              </button>
            ) : (
              <p className="text-[10px] text-[#52525B] text-center">{getText('complete_all')}</p>
            )}
          </div>
        );
      })()}

      {/* Channel Subscribe */}
      {(() => {
        const cs = earnData?.daily?.channel_subscribe;
        if (!cs) return null;

        return (
          <div data-testid="subscribe-section" className="glass-card rounded-2xl p-4 mb-6 border border-[#3B82F6]/15">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Send className="w-5 h-5 text-[#3B82F6]" />
                <h3 className="font-medium text-sm text-[#F5F5F7]">{getText('subscribe_channel')}</h3>
              </div>
              {cs.claimed && (
                <span className="text-xs text-[#10B981]"><Check className="w-3.5 h-3.5 inline" /></span>
              )}
            </div>

            <p className="text-xs text-[#A1A1AA] mb-1">
              {getText('subscribe_desc')}
            </p>
            <p className="text-[10px] text-[#52525B] mb-3">
              +{cs.reward} {getText('coins')} ({language === 'ru' ? 'одноразово' : 'one-time'})
            </p>

            <div className="flex gap-2">
              <a
                href="https://t.me/privateart"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="subscribe-link"
                className="flex-1 py-2.5 rounded-xl bg-[#3B82F6]/15 border border-[#3B82F6]/30 text-[#3B82F6] flex items-center justify-center gap-2 text-sm font-medium transition-all active:scale-95"
                onClick={() => haptic && haptic()}
              >
                <Send className="w-4 h-4" />
                <span>{getText('subscribe_btn')}</span>
              </a>
              {!cs.claimed && (
                <button
                  data-testid="claim-subscribe-btn"
                  onClick={handleClaimSubscribe}
                  disabled={claimingSub}
                  className="flex-1 py-2.5 rounded-xl bg-[#10B981] text-white flex items-center justify-center gap-1.5 text-sm font-medium transition-all active:scale-95"
                >
                  {claimingSub ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <><Gift className="w-4 h-4" /><span>+{cs.reward}</span></>
                  )}
                </button>
              )}
              {cs.claimed && (
                <div className="flex-1 py-2.5 rounded-xl bg-[#141420] flex items-center justify-center gap-1 text-xs text-[#10B981]">
                  <Check className="w-3.5 h-3.5" />
                  <span>{getText('claimed')}</span>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Channel Boost */}
      {(() => {
        const cb = earnData?.daily?.channel_boosts;
        if (!cb) return null;
        const claimable = Math.min(cb.boosts, cb.max) - cb.claimed_count;
        const allClaimed = cb.claimed_count >= cb.max;

        return (
          <div data-testid="boost-section" className="glass-card rounded-2xl p-4 mb-6 border border-[#8B5CF6]/15">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-[#8B5CF6]" />
                <h3 className="font-medium text-sm text-[#F5F5F7]">{getText('boost_channel')}</h3>
              </div>
              <span className="text-xs font-mono text-[#8B5CF6]">
                {Math.min(cb.boosts, cb.max)}/{cb.max}
              </span>
            </div>

            <p className="text-xs text-[#A1A1AA] mb-1">
              {getText('boost_desc')}
            </p>
            <p className="text-[10px] text-[#52525B] mb-3">
              +{cb.reward_per_boost} {getText('coins')} {language === 'ru' ? 'за буст' : 'per boost'}
            </p>

            {/* Boost progress */}
            <div className="grid grid-cols-4 gap-1.5 mb-3">
              {Array.from({ length: cb.max }, (_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full ${
                    i < cb.claimed_count ? 'bg-[#8B5CF6]'
                    : i < cb.boosts ? 'bg-[#8B5CF6]/40'
                    : 'bg-[#1a1a2e]'
                  }`}
                />
              ))}
            </div>

            <div className="flex gap-2">
              <a
                href="https://t.me/boost/privateart"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="boost-link"
                className="flex-1 py-2.5 rounded-xl bg-[#8B5CF6]/15 border border-[#8B5CF6]/30 text-[#8B5CF6] flex items-center justify-center gap-2 text-sm font-medium transition-all active:scale-95"
                onClick={() => haptic && haptic()}
              >
                <Zap className="w-4 h-4" />
                <span>{getText('boost_btn')}</span>
              </a>
              {claimable > 0 && (
                <button
                  data-testid="claim-boosts-btn"
                  onClick={handleClaimBoosts}
                  disabled={claimingBoosts}
                  className="flex-1 py-2.5 rounded-xl bg-[#8B5CF6] text-white flex items-center justify-center gap-1.5 text-sm font-medium transition-all active:scale-95"
                >
                  {claimingBoosts ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <><Gift className="w-4 h-4" /><span>+{claimable * cb.reward_per_boost}</span></>
                  )}
                </button>
              )}
              {allClaimed && cb.boosts >= cb.max && (
                <div className="flex-1 py-2.5 rounded-xl bg-[#141420] flex items-center justify-center gap-1 text-xs text-[#10B981]">
                  <Check className="w-3.5 h-3.5" />
                  <span>{getText('claimed')}</span>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Channel Gift */}
      {(() => {
        const cg = earnData?.daily?.channel_gift;
        if (!cg) return null;

        return (
          <div data-testid="gift-section" className="glass-card rounded-2xl p-4 mb-6 border border-[#EC4899]/15">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Gift className="w-5 h-5 text-[#EC4899]" />
                <h3 className="font-medium text-sm text-[#F5F5F7]">{getText('gift_channel')}</h3>
              </div>
              {cg.claimed && (
                <span className="text-xs text-[#10B981]"><Check className="w-3.5 h-3.5 inline" /></span>
              )}
            </div>

            <p className="text-xs text-[#A1A1AA] mb-1">
              {getText('gift_desc')}
            </p>
            <p className="text-[10px] text-[#52525B] mb-3">
              +{cg.reward} {getText('coins')} ({language === 'ru' ? 'одноразово' : 'one-time'})
            </p>

            <div className="flex gap-2">
              <a
                href="https://t.me/privateart"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="gift-link"
                className="flex-1 py-2.5 rounded-xl bg-[#EC4899]/15 border border-[#EC4899]/30 text-[#EC4899] flex items-center justify-center gap-2 text-sm font-medium transition-all active:scale-95"
                onClick={() => haptic && haptic()}
              >
                <Gift className="w-4 h-4" />
                <span>{getText('gift_btn')}</span>
              </a>
              {cg.has_gift && !cg.claimed && (
                <button
                  data-testid="claim-gift-btn"
                  onClick={handleClaimGift}
                  disabled={claimingGift}
                  className="flex-1 py-2.5 rounded-xl bg-[#EC4899] text-white flex items-center justify-center gap-1.5 text-sm font-medium transition-all active:scale-95"
                >
                  {claimingGift ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <><Gift className="w-4 h-4" /><span>+{cg.reward}</span></>
                  )}
                </button>
              )}
              {cg.claimed && (
                <div className="flex-1 py-2.5 rounded-xl bg-[#141420] flex items-center justify-center gap-1 text-xs text-[#10B981]">
                  <Check className="w-3.5 h-3.5" />
                  <span>{getText('claimed')}</span>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Achievements Section */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Trophy className="w-4 h-4 text-[#D4AF37]" />
          <h2 className="text-sm font-medium text-[#A1A1AA] uppercase tracking-wider">{getText('achievements')}</h2>
        </div>

        {/* Same Artist */}
        <AchievementCard
          icon={<Palette className="w-5 h-5 text-[#D4AF37]" />}
          title={getText('same_artist')}
          description={getText('collect_5_artist')}
          progress={achievements?.same_artist?.progress || 0}
          target={5}
          reward={achievements?.same_artist?.reward ?? 10000000}
          bestName={achievements?.same_artist?.best_name}
          completed={achievements?.same_artist?.completed}
          claimed={achievements?.same_artist?.claimed}
          onClaim={() => handleClaimAchievement('same_artist_5')}
          claiming={claimingAch === 'same_artist_5'}
          getText={getText}
        />

        {/* Same Year */}
        <AchievementCard
          icon={<CalendarDays className="w-5 h-5 text-[#D4AF37]" />}
          title={getText('same_year')}
          description={getText('collect_5_year')}
          progress={achievements?.same_year?.progress || 0}
          target={5}
          reward={achievements?.same_year?.reward ?? 1000000}
          bestName={achievements?.same_year?.best_name}
          completed={achievements?.same_year?.completed}
          claimed={achievements?.same_year?.claimed}
          onClaim={() => handleClaimAchievement('same_year_5')}
          claiming={claimingAch === 'same_year_5'}
          getText={getText}
        />
      </div>

      {/* Card reveal after claiming all-tasks bonus (+2500 coins + Rare painting) */}
      {revealedTaskCard && (
        <CardRevealModal
          card={revealedTaskCard}
          onComplete={async () => {
            setRevealedTaskCard(null);
            await refreshUser();
          }}
          language={language}
        />
      )}

      {/* Discover: in-app artwork preview (keeps bot in chat list for
          Direct-Link users — see components/DiscoverPreviewModal.js) */}
      {discoverPreview && (
        <DiscoverPreviewModal
          card={discoverPreview}
          reward={earnData?.daily?.discover?.reward_per_card}
          language={language}
          onClaimed={handleDiscoverClaimed}
          onClose={() => setDiscoverPreview(null)}
          tutorialMode={tutorialStep === 'discover_claim'}
        />
      )}

    </div>
  );
};

const ShopSection = ({ language, user, refreshUser }) => {
  const [items, setItems] = useState([]);
  const [purchasing, setPurchasing] = useState(null);

  useEffect(() => {
    api.get('/shop/items')
      .then(res => { setItems(res.data.items); })
      .catch(() => {});
  }, []);

  const handlePurchase = async (item) => {
    if (!user?.id || purchasing) return;
    setPurchasing(item.id);
    try {
      const res = await api.post(`/shop/purchase/invoice`, { item_id: item.id });
      const invoiceLink = res.data?.invoice_link;
      if (!invoiceLink) throw new Error('No invoice');
      
      const tg = window.Telegram?.WebApp;
      if (tg?.openInvoice) {
        tg.openInvoice(invoiceLink, async (status) => {
          if (status === 'paid') {
            await api.post(`/shop/purchase/confirm`, { item_id: item.id });
            await refreshUser();
          }
          setPurchasing(null);
        });
      } else {
        window.open(invoiceLink, '_blank');
        setPurchasing(null);
      }
    } catch (err) {
      console.error('Purchase error:', err);
      setPurchasing(null);
    }
  };

  if (!items.length) return null;

  return (
    <div className="space-y-2">
      {items.map((item) => (
        <div key={item.id} data-testid={`shop-item-${item.id}`} className="glass-card rounded-xl p-3 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-[#F5F5F7]">{item.name}</p>
            <p className="text-[10px] text-[#A1A1AA]">{item.coins.toLocaleString()} coins</p>
          </div>
          <button
            onClick={() => handlePurchase(item)}
            disabled={purchasing === item.id}
            className="shrink-0 px-3 py-1.5 rounded-lg bg-[#5B68F6]/15 border border-[#5B68F6]/30 text-[#7B8AFF] text-xs font-medium hover:bg-[#5B68F6]/25 transition-all active:scale-95 disabled:opacity-50"
          >
            {purchasing === item.id ? '...' : `⭐ ${item.price_stars}`}
          </button>
        </div>
      ))}
      <p className="text-[9px] text-[#52525B] text-center mt-2">
        {language === 'ru' ? 'Оплата через Telegram Stars' : 'Payments via Telegram Stars'}
      </p>
    </div>
  );
};

const AchievementCard = ({ icon, title, description, progress, target, reward, bestName, completed, claimed, onClaim, claiming, getText }) => {
  return (
    <div className="glass-card rounded-2xl p-4 mb-3" data-testid={`achievement-${title}`}>
      <div className="flex items-center gap-3 mb-2">
        {icon}
        <div className="flex-1">
          <h3 className="font-medium text-[#F5F5F7] text-sm">{title}</h3>
          <p className="text-[10px] text-[#A1A1AA]">{description}</p>
        </div>
        <div className="text-right">
          <div className="flex items-center gap-0.5">
            <Coins className="w-3 h-3 text-[#D4AF37]" />
            <span className="font-mono text-sm text-[#D4AF37]">{reward.toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-2">
        <div className="flex justify-between text-[10px] text-[#A1A1AA] mb-1">
          <span>{bestName ? `${bestName}` : ''}</span>
          <span>{Math.min(progress, target)}/{target}</span>
        </div>
        <div className="h-1.5 bg-[#141420] rounded-full overflow-hidden">
          <div
            className="h-full bg-[#D4AF37] rounded-full transition-all duration-500"
            style={{ width: `${Math.min((progress / target) * 100, 100)}%` }}
          />
        </div>
      </div>

      {completed && !claimed && (
        <button
          onClick={onClaim}
          disabled={claiming}
          className="w-full py-2 rounded-lg bg-[#10B981] text-white text-sm font-medium flex items-center justify-center gap-1.5 transition-all active:scale-95"
        >
          <Gift className="w-4 h-4" />
          <span>{getText('claim_rewards')}</span>
        </button>
      )}
      {claimed && (
        <div className="text-center py-1.5">
          <span className="text-xs text-[#10B981]"><Check className="w-3 h-3 inline mr-1" />{getText('claimed')}</span>
        </div>
      )}
    </div>
  );
};

export default EarnPage;
